import { EmojiReactionsIcon } from '@daily/shared/components/Icons/EmojiReactionsIcon';
import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useMediaQuery } from '@daily/shared/hooks/useMediaQuery';
import { isElementVisibleInViewport } from '@daily/shared/lib/dom';
import classnames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useCallConfig } from '../../hooks/useCallConfig';
import {
  useEmojiReactions,
  useShowEmojiReactionsMenu,
} from '../../hooks/useEmojiReactions';
import { useIsMobile } from '../../lib/state/core';
import { useMobileTray } from '../../lib/state/layout';
import { TOOLTIP_DELAY } from './constant';
import { EmojiReactionsMenu } from './EmojiReactionsMenu';
import { TrayButton } from './TrayButton';

interface Props extends React.ComponentProps<typeof TrayButton> {}

export const EmojiReactionControls: React.FC<Props> = ({
  className,
  onClick,
  ...props
}) => {
  const { t } = useTranslation();
  const { enableEmojiReactions } = useCallConfig();
  const { mediaQueries } = useTheme();
  const { sendReaction } = useEmojiReactions();
  const { setShowEmojiReactionsMenu, showEmojiReactionsMenu } =
    useShowEmojiReactionsMenu();
  const [isMobile] = useIsMobile();
  const isMobileVP = useMediaQuery(mediaQueries.small);
  const [showMobileTray] = useMobileTray();

  const handleButtonClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    setShowEmojiReactionsMenu(true);
    onClick?.(ev);
  };

  const handleClickReaction = useCallback(
    (reaction: string) => {
      // Set custom position for the own emoji during the fly-up animation
      let xPosition: number;
      const controlsButtonElement = document.getElementById(
        props?.id ?? 'emoji-reaction-controls'
      );

      xPosition = isElementVisibleInViewport(controlsButtonElement)
        ? controlsButtonElement.getBoundingClientRect().x
        : document.getElementById('tray-more-btn').getBoundingClientRect().x;

      sendReaction(reaction, xPosition);
    },
    [props?.id, sendReaction]
  );

  const handleMenuClose = useCallback(() => {
    setShowEmojiReactionsMenu(false);
  }, [setShowEmojiReactionsMenu]);

  useEffect(() => {
    if (!showMobileTray) {
      handleMenuClose();
    }
  }, [handleMenuClose, showMobileTray]);

  if (!enableEmojiReactions) return null;

  return (
    <div className="emoji-reaction-controls">
      <Tooltip
        title={t('emojiReactions.sendReaction')}
        delay={TOOLTIP_DELAY}
        id="tt-emoji-reaction-controls"
        tabIndex={-1}
      >
        <TrayButton
          className={classnames(className)}
          onClick={handleButtonClick}
          {...props}
          id={props?.id ?? 'emoji-reaction-controls'}
        >
          <EmojiReactionsIcon size={24} />
          <Text>{t('emojiReactions.react')}</Text>
        </TrayButton>
      </Tooltip>
      {showEmojiReactionsMenu && (
        <EmojiReactionsMenu
          align={!isMobile && isMobileVP ? 'right' : 'center'}
          aria-labelledby={props?.id ?? 'emoji-reaction-controls'}
          id="emoji-reaction-menu"
          onClickReaction={handleClickReaction}
          onClose={handleMenuClose}
        />
      )}
    </div>
  );
};
